import type {
  FilterBarItem,
  FilterItem,
} from '@wconcept/wconcept-common/models/renewal/common';
import type {
  LpProductFilterType,
  LpProductFilterOption,
  LpProductFilters,
  LpProductGroups,
  LpProductGroupType,
  LpProductPriceRange,
  LpProductRequest,
} from '@apis/renewal/lpProduct';
import { getLpProductBrandOptions } from '@apis/renewal/lpProduct';
import { getLpCategoryRequest } from '@utils/lpCategoryRequest';

const FILTER_TYPE_BY_TAB: Readonly<Record<string, LpProductFilterType>> = {
  category: 'category',
  color: 'colors',
  style: 'styles',
  tpo: 'tpos',
  priceRange: 'price',
  benefit: 'products',
  brand: 'brand',
  topBrand: 'top-brand',
  newBrand: 'new-brand',
};

const FILTER_QUERY_KEYS = [
  'category',
  'brand',
  'topBrand',
  'newBrand',
  'color',
  'price',
  'productType',
  'benefit',
  'products',
  'saleTags',
  'style',
  'tpo',
  'page',
] as const;

const EMPTY_FILTER_STATE_BY_TAB = {
  brand: {
    className: 'coner-brand',
    message: '해당되는 브랜드가 없습니다.<br>선택한 필터를 변경해보세요.',
  },
  color: {
    className: 'coner-color',
    message: '해당되는 색상이 없습니다.<br>선택한 필터를 변경해보세요.',
  },
  style: {
    className: 'coner-style',
    message: '해당되는 스타일이 없습니다.<br>선택한 필터를 변경해보세요.',
  },
  tpo: {
    className: 'coner-timeplace',
    message: '해당되는 TPO가 없습니다.<br>선택한 필터를 변경해보세요.',
  },
} as const;

const getCodes = (
  selectedFilters: readonly FilterItem[],
  filterType: string
): string[] =>
  selectedFilters.flatMap(filter =>
    filter.filterType === filterType && filter.code ? [filter.code] : []
  );

const getPriceRange = (
  selectedFilters: readonly FilterItem[]
): readonly number[] | undefined => {
  const priceFilter = selectedFilters.find(
    filter => filter.filterType === 'priceRange'
  );

  return Array.isArray(priceFilter?.value) ? priceFilter.value : undefined;
};

export const toggleLpProductV2CategoryFilter = (
  selectedFilters: readonly FilterItem[],
  item: FilterItem
): FilterItem[] => {
  const isSelected = selectedFilters.some(
    filter => filter.filterType === 'category' && filter.code === item.code
  );

  if (isSelected) {
    return selectedFilters.filter(
      filter => filter.filterType !== 'category' || filter.code !== item.code
    );
  }

  return [...selectedFilters, { ...item, filterType: 'category' }];
};

export const selectLpProductV2CategoryPath = (
  selectedFilters: readonly FilterItem[],
  item: FilterItem
): FilterItem[] => {
  const nonCategoryFilters = selectedFilters.filter(
    filter => filter.filterType !== 'category'
  );

  if (item.code === 'all') return nonCategoryFilters;

  const categoryFilters = selectedFilters.filter(
    filter => filter.filterType === 'category'
  );
  const selectedIndex = categoryFilters.findIndex(
    filter => filter.code === item.code
  );

  if (selectedIndex >= 0) {
    return [
      ...categoryFilters.slice(0, selectedIndex + 1),
      ...nonCategoryFilters,
    ];
  }

  return [{ ...item, filterType: 'category' }, ...nonCategoryFilters];
};

const getBrandCodes = (selectedFilters: readonly FilterItem[]): number[] =>
  ['brand', 'topBrand', 'newBrand'].flatMap(filterType =>
    getCodes(selectedFilters, filterType).flatMap(code => {
      const brandCode = Number(code);
      return Number.isFinite(brandCode) ? [brandCode] : [];
    })
  );

export const toggleLpProductV2BrandFilter = (
  selectedFilters: readonly FilterItem[],
  filterType: string,
  item: FilterItem
): FilterItem[] => {
  const brandFilterTypes = ['brand', 'topBrand', 'newBrand'];
  const isSelected = selectedFilters.some(
    filter =>
      brandFilterTypes.includes(filter.filterType ?? '') &&
      filter.code === item.code
  );

  if (isSelected) {
    return selectedFilters.filter(
      filter =>
        !brandFilterTypes.includes(filter.filterType ?? '') ||
        filter.code !== item.code
    );
  }

  return [...selectedFilters, { ...item, filterType }];
};

export const sortLpProductV2BrandFilterItems = (
  items: readonly FilterItem[],
  selectedFilters: readonly FilterItem[]
): FilterItem[] => {
  const brandFilterTypes = ['brand', 'topBrand', 'newBrand'];
  const selectedBrandCodes = new Set(
    selectedFilters.flatMap(filter =>
      filter.code && brandFilterTypes.includes(filter.filterType ?? '')
        ? [filter.code]
        : []
    )
  );

  return [
    ...items.filter(item => item.code && selectedBrandCodes.has(item.code)),
    ...items.filter(item => !item.code || !selectedBrandCodes.has(item.code)),
  ];
};

const getQueryCodes = (value: string | null): string[] =>
  value?.split(',').filter(Boolean) ?? [];

const getQueryBrandCodes = (searchParams: Pick<URLSearchParams, 'get'>) =>
  ['brand', 'topBrand', 'newBrand'].flatMap(key =>
    getQueryCodes(searchParams.get(key)).flatMap(code => {
      const brandCode = Number(code);
      return Number.isFinite(brandCode) ? [brandCode] : [];
    })
  );

export const getLpProductV2FilterType = (
  tabValue: string
): LpProductFilterType | undefined => FILTER_TYPE_BY_TAB[tabValue];

export const isLpProductV2FilterBarItemSelected = (
  item: Pick<FilterBarItem, 'value' | 'subType'>,
  selectedFilters: readonly Pick<FilterItem, 'filterType'>[]
): boolean => {
  const filterTypes =
    item.subType ??
    (item.value === 'benefit' ? ['productType', 'benefit'] : [item.value]);

  return selectedFilters.some(filter =>
    filterTypes.includes(filter.filterType ?? '')
  );
};

export const getLpProductV2RetryFilterTab = (
  filterBarList: readonly FilterBarItem[],
  selectedFilters: readonly Pick<FilterItem, 'filterType'>[] = [],
  lastFilterTab?: string
): FilterBarItem | undefined => {
  const tabs = filterBarList.filter(
    item => item.visible !== false && getLpProductV2FilterType(item.value)
  );
  const firstSelectedFilter = selectedFilters.slice(0, 1);

  return (
    tabs.find(item => item.value === lastFilterTab) ??
    tabs.find(item =>
      isLpProductV2FilterBarItemSelected(item, firstSelectedFilter)
    ) ??
    tabs[0]
  );
};

export const getLpProductV2EmptyState = (
  tabValue: string,
  filters: Pick<
    LpProductFilters,
    'brand' | 'brands' | 'topBrand' | 'newBrand' | 'colors' | 'styles' | 'tpos'
  >,
  selectedFilters: readonly Pick<FilterItem, 'filterType'>[] = []
):
  | (typeof EMPTY_FILTER_STATE_BY_TAB)[keyof typeof EMPTY_FILTER_STATE_BY_TAB]
  | undefined => {
  switch (tabValue) {
    case 'brand':
    case 'topBrand':
    case 'newBrand': {
      const hasBrandOptions = [
        ...(getLpProductBrandOptions(filters) ?? []),
        ...(filters.topBrand ?? []),
        ...(filters.newBrand ?? []),
      ].some(option => option.count > 0);
      const hasSelectedBrand = selectedFilters.some(filter =>
        ['brand', 'topBrand', 'newBrand'].includes(filter.filterType ?? '')
      );

      return hasBrandOptions || hasSelectedBrand
        ? undefined
        : EMPTY_FILTER_STATE_BY_TAB.brand;
    }
    case 'color':
      return filters.colors?.length
        ? undefined
        : EMPTY_FILTER_STATE_BY_TAB.color;
    case 'style':
      return filters.styles?.length
        ? undefined
        : EMPTY_FILTER_STATE_BY_TAB.style;
    case 'tpo':
      return filters.tpos?.length ? undefined : EMPTY_FILTER_STATE_BY_TAB.tpo;
    default:
      return undefined;
  }
};

/**
 * V2 선택값을 신규 LP API 요청 필드로 직렬화한다.
 * URL에서 분리한 productType과 benefit을 API의 products 배열로 합친다.
 */
export const getLpProductV2Request = (
  selectedFilters: readonly FilterItem[]
): LpProductRequest => {
  const priceRange = getPriceRange(selectedFilters);
  const saleTags = getCodes(selectedFilters, 'saleTags');

  return {
    ...getLpCategoryRequest(getCodes(selectedFilters, 'category')),
    bcds: getBrandCodes(selectedFilters),
    ...(priceRange ? { minPrice: priceRange[0], maxPrice: priceRange[1] } : {}),
    colors: getCodes(selectedFilters, 'color'),
    styles: getCodes(selectedFilters, 'style'),
    tpos: getCodes(selectedFilters, 'tpo'),
    products: [
      ...getCodes(selectedFilters, 'productType'),
      ...getCodes(selectedFilters, 'benefit'),
    ],
    ...(saleTags.length > 0 ? { saleTags } : {}),
  };
};

export const getLpProductV2RequestFromSearchParams = (
  searchParams: Pick<URLSearchParams, 'get'>
): LpProductRequest => {
  const [minPrice, maxPrice] = getQueryCodes(searchParams.get('price')).map(
    Number
  );
  const saleTags = getQueryCodes(searchParams.get('saleTags'));

  return {
    ...getLpCategoryRequest(getQueryCodes(searchParams.get('category'))),
    bcds: getQueryBrandCodes(searchParams),
    ...(Number.isFinite(minPrice) ? { minPrice } : {}),
    ...(Number.isFinite(maxPrice) ? { maxPrice } : {}),
    colors: getQueryCodes(searchParams.get('color')),
    styles: getQueryCodes(searchParams.get('style')),
    tpos: getQueryCodes(searchParams.get('tpo')),
    products: [
      ...getQueryCodes(searchParams.get('productType')),
      ...getQueryCodes(searchParams.get('benefit')),
    ],
    ...(saleTags.length > 0 ? { saleTags } : {}),
  };
};

const getFilterOptionsWithUpdatedCounts = <TCode extends string | number>(
  originalOptions: readonly LpProductFilterOption<TCode>[],
  updatedOptions: readonly LpProductFilterOption[] | null | undefined
): readonly LpProductFilterOption<TCode>[] => {
  const countsByCode = new Map<string, number>();

  (updatedOptions ?? []).forEach(option => {
    countsByCode.set(String(option.code), option.count);
  });

  return originalOptions.map(option => ({
    ...option,
    count: countsByCode.get(String(option.code)) ?? 0,
  }));
};

const getFilterOptionsWithMissingSelected = <TCode extends string | number>(
  originalOptions: readonly LpProductFilterOption<TCode>[],
  updatedOptions: readonly LpProductFilterOption<TCode>[] | null | undefined,
  selectedCodes: readonly (string | number)[]
): readonly LpProductFilterOption<TCode>[] => {
  const updatedCodes = new Set(
    (updatedOptions ?? []).map(option => String(option.code))
  );
  const selectedCodeSet = new Set(selectedCodes.map(String));
  const missingSelectedOptions = originalOptions
    .filter(
      option =>
        selectedCodeSet.has(String(option.code)) &&
        !updatedCodes.has(String(option.code))
    )
    .map(option => ({ ...option, count: 0 }));

  return [...missingSelectedOptions, ...(updatedOptions ?? [])];
};

const getBrandFilterOptionsWithMissingSelected = (
  originalOptions: readonly LpProductFilterOption<number>[],
  updatedOptions: readonly LpProductFilterOption<number>[] | null | undefined,
  selectedFilters: readonly FilterItem[],
  filterTypes: readonly string[]
): readonly LpProductFilterOption<number>[] => {
  const selectedBrands = selectedFilters.filter(
    filter =>
      filter.code &&
      filter.name &&
      filterTypes.includes(filter.filterType ?? '')
  );
  const restoredOptions = getFilterOptionsWithMissingSelected(
    originalOptions,
    updatedOptions,
    selectedBrands.flatMap(filter => (filter.code ? [filter.code] : []))
  );
  const restoredCodes = new Set(
    restoredOptions.map(option => String(option.code))
  );
  const missingSelectedBrands = selectedBrands.flatMap(filter => {
    const code = Number(filter.code);

    if (!Number.isFinite(code) || restoredCodes.has(String(code))) return [];

    restoredCodes.add(String(code));
    return [{ code, name: filter.name ?? '', count: 0 }];
  });

  return [...missingSelectedBrands, ...restoredOptions];
};

const getProductGroupsWithUpdatedCounts = (
  originalGroups: LpProductGroups,
  updatedGroups: LpProductGroups | null | undefined
): LpProductGroups => ({
  ...(originalGroups.productType
    ? {
        productType: getFilterOptionsWithUpdatedCounts(
          originalGroups.productType,
          updatedGroups?.productType
        ),
      }
    : {}),
  ...(originalGroups.benefit
    ? {
        benefit: getFilterOptionsWithUpdatedCounts(
          originalGroups.benefit,
          updatedGroups?.benefit
        ),
      }
    : {}),
});

export const getLpProductV2FiltersWithUpdatedCounts = (
  originalFilters: LpProductFilters,
  updatedFilters: LpProductFilters,
  selectedFilters: readonly FilterItem[] = []
): LpProductFilters => ({
  ...updatedFilters,
  ...(originalFilters.colors
    ? {
        colors: getFilterOptionsWithUpdatedCounts(
          originalFilters.colors,
          updatedFilters.colors
        ),
      }
    : {}),
  ...(originalFilters.styles
    ? {
        styles: getFilterOptionsWithUpdatedCounts(
          originalFilters.styles,
          updatedFilters.styles
        ),
      }
    : {}),
  ...(originalFilters.tpos
    ? {
        tpos: getFilterOptionsWithUpdatedCounts(
          originalFilters.tpos,
          updatedFilters.tpos
        ),
      }
    : {}),
  ...(getLpProductBrandOptions(originalFilters) ||
  getBrandCodes(selectedFilters).length > 0
    ? {
        brands: getBrandFilterOptionsWithMissingSelected(
          getLpProductBrandOptions(originalFilters) ?? [],
          getLpProductBrandOptions(updatedFilters),
          selectedFilters,
          ['brand', 'topBrand', 'newBrand']
        ),
      }
    : {}),
  ...(originalFilters.topBrand ||
  getCodes(selectedFilters, 'topBrand').length > 0
    ? {
        topBrand: getBrandFilterOptionsWithMissingSelected(
          originalFilters.topBrand ?? [],
          updatedFilters.topBrand,
          selectedFilters,
          ['topBrand']
        ),
      }
    : {}),
  ...(originalFilters.newBrand ||
  getCodes(selectedFilters, 'newBrand').length > 0
    ? {
        newBrand: getBrandFilterOptionsWithMissingSelected(
          originalFilters.newBrand ?? [],
          updatedFilters.newBrand,
          selectedFilters,
          ['newBrand']
        ),
      }
    : {}),
  ...(originalFilters.products
    ? {
        products: getProductGroupsWithUpdatedCounts(
          originalFilters.products,
          updatedFilters.products
        ),
      }
    : {}),
});

export const mergeLpProductV2FilterResult = (
  currentFilters: LpProductFilters,
  updatedFilters: LpProductFilters,
  filterType: LpProductFilterType
): LpProductFilters => {
  switch (filterType) {
    case 'category':
      return {
        ...currentFilters,
        category: updatedFilters.category,
        rootCategory: updatedFilters.rootCategory,
      };
    case 'colors':
      return { ...currentFilters, colors: updatedFilters.colors };
    case 'styles':
      return { ...currentFilters, styles: updatedFilters.styles };
    case 'tpos':
      return { ...currentFilters, tpos: updatedFilters.tpos };
    case 'price':
      return { ...currentFilters, priceRange: updatedFilters.priceRange };
    case 'brand':
      return {
        ...currentFilters,
        brand: updatedFilters.brand,
        brands: updatedFilters.brands,
      };
    case 'top-brand':
      return { ...currentFilters, topBrand: updatedFilters.topBrand };
    case 'new-brand':
      return { ...currentFilters, newBrand: updatedFilters.newBrand };
    case 'products':
      return { ...currentFilters, products: updatedFilters.products };
    default: {
      const exhaustiveFilterType: never = filterType;
      return exhaustiveFilterType;
    }
  }
};

export const getLpProductV2QueryString = (
  searchParams: URLSearchParams,
  selectedFilters: readonly FilterItem[]
): string => {
  const nextSearchParams = new URLSearchParams(searchParams);
  FILTER_QUERY_KEYS.forEach(key => nextSearchParams.delete(key));

  const category = getCodes(selectedFilters, 'category');
  const brands = getCodes(selectedFilters, 'brand');
  const topBrands = getCodes(selectedFilters, 'topBrand');
  const newBrands = getCodes(selectedFilters, 'newBrand');
  const colors = getCodes(selectedFilters, 'color');
  const productTypes = getCodes(selectedFilters, 'productType');
  const benefits = getCodes(selectedFilters, 'benefit');
  const saleTags = getCodes(selectedFilters, 'saleTags');
  const styles = getCodes(selectedFilters, 'style');
  const tpos = getCodes(selectedFilters, 'tpo');
  const priceRange = getPriceRange(selectedFilters);

  if (category.length > 0) nextSearchParams.set('category', category.join(','));
  if (brands.length > 0) nextSearchParams.set('brand', brands.join(','));
  if (topBrands.length > 0) {
    nextSearchParams.set('topBrand', topBrands.join(','));
  }
  if (newBrands.length > 0) {
    nextSearchParams.set('newBrand', newBrands.join(','));
  }
  if (colors.length > 0) nextSearchParams.set('color', colors.join(','));
  if (productTypes.length > 0) {
    nextSearchParams.set('productType', productTypes.join(','));
  }
  if (benefits.length > 0) nextSearchParams.set('benefit', benefits.join(','));
  if (saleTags.length > 0) nextSearchParams.set('saleTags', saleTags.join(','));
  if (styles.length > 0) nextSearchParams.set('style', styles.join(','));
  if (tpos.length > 0) nextSearchParams.set('tpo', tpos.join(','));
  if (priceRange) nextSearchParams.set('price', priceRange.join(','));

  return nextSearchParams.toString();
};

export const getLpProductV2PriceDomain = (
  priceRange?: LpProductPriceRange | null
): number[] => (priceRange ? [priceRange.min, priceRange.limit] : []);

export const getLpProductV2QuickFilterQueryString = (
  searchParams: URLSearchParams,
  checked: boolean,
  code: string,
  queryKey: LpProductGroupType | 'saleTags'
): string => {
  const nextSearchParams = new URLSearchParams(searchParams);
  const currentCodes = getQueryCodes(nextSearchParams.get(queryKey));
  const nextCodes = checked
    ? [...new Set([...currentCodes, code])]
    : currentCodes.filter(currentCode => currentCode !== code);

  nextSearchParams.delete('page');

  if (nextCodes.length > 0) {
    nextSearchParams.set(queryKey, nextCodes.join(','));
  } else {
    nextSearchParams.delete(queryKey);
  }

  return nextSearchParams.toString();
};
